"use client";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { ScrambleTextPlugin } from "gsap/ScrambleTextPlugin";

if (typeof window !== "undefined") {
  gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText, ScrambleTextPlugin);
  if (process.env.NODE_ENV !== "production") {
    // Dev console access for verifying layout in an occluded/automated browser
    // (rAF pauses there, freezing tweens mid-flight): window.__ffwd() fires all
    // pending ScrollTriggers and jumps every animation to its end state.
    const w = window as Window & { gsap?: typeof gsap; __ffwd?: () => void };
    w.gsap = gsap;
    w.__ffwd = () => {
      ScrollTrigger.getAll().forEach((st) => {
        const anim = st.animation;
        if (anim && anim.totalDuration() !== Infinity) anim.totalProgress(1);
      });
      gsap.globalTimeline
        .getChildren(true, true, true)
        .forEach((child) => {
          if (child.totalDuration() !== Infinity) child.totalProgress(1);
        });
    };
  }
}

/** Shared entrance ease — confident exponential settle, no bounce. */
export const EASE_OUT = "power4.out";
/** Standard "section has scrolled into view" trigger start. */
export const ENTER_START = "top 82%";
export const NOT_REDUCED = "(prefers-reduced-motion: no-preference)";

export { gsap, useGSAP, ScrollTrigger, SplitText };

/**
 * Self-healing for entrances: GSAP's ticker runs on rAF, which browsers
 * starve (tab switches, HMR, bfcache restores, occlusion) — a starved ticker
 * leaves a from-tween stuck half-revealed, i.e. invisible content. Call this
 * when an entrance STARTS (e.g. ScrollTrigger onEnter): a real-clock timer
 * hard-completes the animation if it hasn't finished on its own.
 */
export function insure(anim: gsap.core.Animation | null | undefined, extraMs = 1500) {
  if (!anim) return;
  window.setTimeout(
    () => {
      if (anim.progress() < 1) anim.progress(1);
    },
    anim.totalDuration() * 1000 + extraMs,
  );
}

/**
 * Play a paused entrance animation the first time `el` intersects the
 * viewport, then arm the insurance. IntersectionObserver instead of
 * ScrollTrigger on purpose: ST's refresh cycle can re-render a from-tween
 * back to its hidden start state after a late refresh (fonts/images/layout
 * shifts), and `once: true` triggers created while the page is already
 * scrolled past them die without ever firing — both left content invisible.
 * IO has no refresh cycle and reports current state on observe. Returns a
 * disposer (return it from the gsap.matchMedia handler for cleanup).
 *
 * Three layered checks converge on one play, guarded so only the first
 * ever fires:
 *
 * 1. Synchronous at-hookup check — reads `el`'s CURRENT position before
 *    anything else is wired up, and plays immediately if it's already at or
 *    past the trigger line. Validated root cause: IntersectionObserver
 *    delivers ZERO callbacks — not even its "report current state on
 *    observe" initial one — while `document.hidden` is true (confirmed
 *    live: an IO observing an element already scrolled past the trigger
 *    line received no callback after 5s of a hidden document). Hookup can
 *    happen while hidden — a backgrounded tab, prerendering, a
 *    bfcache-restored tab whose scroll position is applied before the page
 *    repaints — or hydration/effect-mount can simply lag on a slow device
 *    while an eager visitor keeps scrolling past sections before their GSAP
 *    effects ever attach. A direct getBoundingClientRect() read works the
 *    same whether or not the document is hidden (layout still runs for an
 *    unpainted document), so it catches this case IO structurally cannot.
 *    But it only runs once, synchronously, at setup — it can't catch a
 *    crossing that happens *after* hookup while IO itself is starved.
 * 2. IntersectionObserver — the normal, cheap, event-driven path for the
 *    common case: hookup happens well before the element is reached, and
 *    the visitor scrolls down into it later while the document is visible.
 * 3. A passive scroll listener (ground truth) — belt-and-braces for
 *    everything 1+2 can still miss: IO callbacks are dispatched off the
 *    same starved rAF/task queue as GSAP's ticker, so a backgrounded tab,
 *    heavy main-thread work, or an occluded-but-not-hidden window can defer
 *    them indefinitely even though real scroll events keep arriving (e.g. a
 *    programmatic `scrollTo` + dispatched `scroll` in an automated/occluded
 *    browser). On every scroll it re-reads the same getBoundingClientRect()
 *    check as path 1 against the same trigger line and fires if reached.
 *    Throttled with a real-clock `setTimeout` (150ms), NOT `rAF` — rAF is
 *    exactly the mechanism this path exists to route around (see `insure`'s
 *    same real-clock reasoning above), so throttling through it would
 *    reintroduce the starvation this listener is meant to catch.
 *
 * All paths funnel through a single `fire()` that plays at most once and
 * tears down every listener/observer that was set up (also torn down by the
 * returned disposer, e.g. from a gsap.matchMedia cleanup, on unmount before
 * any of them fired).
 */
export function playOnEnter(
  anim: gsap.core.Animation | null | undefined,
  el: Element | null | undefined,
) {
  if (!anim) return undefined;
  if (!el) {
    anim.play();
    insure(anim);
    return undefined;
  }

  // Mirrors the -15% bottom rootMargin below: "already past the line" once
  // the element's top has scrolled up past 85% of the viewport height. Guard
  // against a zero-size rect (element not currently rendered, e.g. hidden
  // below the mobile breakpoint) — that's "not yet in view", not "past it".
  const pastTriggerLine = (rect: DOMRect) =>
    (rect.width > 0 || rect.height > 0) && rect.top < window.innerHeight * 0.85;

  if (pastTriggerLine(el.getBoundingClientRect())) {
    anim.play();
    insure(anim);
    return undefined;
  }

  let played = false;
  let scrollTimer: number | undefined;

  const cleanup = () => {
    io.disconnect();
    window.removeEventListener("scroll", onScroll);
    if (scrollTimer !== undefined) window.clearTimeout(scrollTimer);
  };

  const fire = () => {
    if (played) return;
    played = true;
    cleanup();
    anim.play();
    insure(anim);
  };

  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) fire();
    },
    // Fire once ~15% of the viewport height remains below the element's top —
    // matches the old "top 82%" trigger line.
    { rootMargin: "0px 0px -15% 0px" },
  );
  io.observe(el);

  const onScroll = () => {
    if (played || scrollTimer !== undefined) return;
    // Real-clock throttle on purpose — see reasoning above (path 3).
    scrollTimer = window.setTimeout(() => {
      scrollTimer = undefined;
      if (!played && pastTriggerLine(el.getBoundingClientRect())) fire();
    }, 150);
  };
  window.addEventListener("scroll", onScroll, { passive: true });

  return cleanup;
}

/**
 * Once-on-enter staggered rise for the elements matching `selector` inside
 * `scope`. The workhorse entrance for grids/lists (bento tiles, logo tiles,
 * quote cards). Skipped entirely under prefers-reduced-motion — content just
 * renders in place.
 *
 * IMPORTANT (grid-paper): never point this at a `[data-grid-block]` element
 * itself — GridClearing measures those with getBoundingClientRect(), which
 * includes ancestor/self transforms. Animate elements INSIDE the block.
 */
export function useEnterStagger(
  scope: React.RefObject<HTMLElement | null>,
  selector: string,
  vars: gsap.TweenVars = {},
) {
  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        const tween = gsap.from(selector, {
          y: 26,
          opacity: 0,
          duration: 0.8,
          ease: EASE_OUT,
          stagger: 0.07,
          paused: true,
          ...vars,
        });
        return playOnEnter(tween, scope.current);
      });
    },
    { scope },
  );
}

/**
 * Masked SplitText line reveal for a heading plus rising sub-elements —
 * SectionHead's self-choreography, extracted so hero headlines (h1) share it.
 * Splits only after fonts.ready (final line breaks), extends each mask's clip
 * box downward so descenders survive tight line-heights, reverts the split on
 * complete (nothing stays clipped at rest), and plays once on viewport enter.
 * No-op under prefers-reduced-motion — content renders in place.
 */
export function useMaskedReveal(
  rootRef: React.RefObject<HTMLElement | null>,
  {
    heading,
    sub = "[data-head-sub]",
    prepare,
    onDone,
  }: {
    /** Selector for the heading to split, e.g. "h2" or "h1". */
    heading: string;
    /** Selector for elements that settle in behind the lines. */
    sub?: string;
    /** Runs right before the timeline is built (e.g. hide a halo). */
    prepare?: (root: HTMLElement) => void;
    /** Runs after the split has reverted (e.g. fade that halo back in). */
    onDone?: (root: HTMLElement) => void;
  },
) {
  useGSAP(
    (_, contextSafe) => {
      const mm = gsap.matchMedia();
      mm.add(NOT_REDUCED, () => {
        let dispose: (() => void) | undefined;
        const play = contextSafe!(() => {
          const root = rootRef.current;
          const h = root?.querySelector<HTMLElement>(heading);
          if (!root || !h) return;
          prepare?.(root);
          const split = SplitText.create(h, { type: "lines", mask: "lines" });
          // Tight line-heights make each mask clip "g"/"y" descenders. Extend
          // every clip box downward (negative margin keeps layout identical).
          (split.masks ?? []).forEach((m) => {
            const el = m as HTMLElement;
            el.style.paddingBottom = "0.18em";
            el.style.marginBottom = "-0.18em";
          });
          const tl = gsap.timeline({
            paused: true,
            onComplete: () => {
              split.revert();
              onDone?.(root);
            },
          });
          tl.from(split.lines, {
            yPercent: 115,
            duration: 0.85,
            stagger: 0.09,
            ease: EASE_OUT,
          });
          const subs = root.querySelectorAll(sub);
          if (subs.length) {
            tl.from(
              subs,
              { y: 16, opacity: 0, duration: 0.7, stagger: 0.12, ease: EASE_OUT },
              0.2,
            );
          }
          dispose = playOnEnter(tl, root);
        });
        // Split only once webfonts have settled, so line breaks are final.
        document.fonts.ready.then(play).catch(play);
        return () => dispose?.();
      });
    },
    { scope: rootRef },
  );
}
